"use client";
/**
 * FitText Component
 * -----------------
 * Automatically resizes text to fit the width of the container.
 * Handles multiple child lines and ensures none of them wrap.
 * Font size is calculated to be as large as possible without any line wrapping.
 *
 * Usage:
 * <FitText
 *   alignVert={true}         // Vertically align text (default: false)
 *   alignHoriz={true}        // Horizontally align text (default: false)
 *   minFontSize={10}         // Minimum font size in px (default: 6)
 *   maxFontSize={60}         // Maximum font size in px (default: 80)
 *   className="my-class"     // Optional custom class
 *   style={{ color: 'red' }} // Optional custom styles
 * >
 *   <div>Line 1</div>
 *   <div>Line 2 which might be longer</div>
 *   <div>Line 3</div>
 * </FitText>
 *
 * Props:
 * - children: ReactNode (required) - The child elements/lines to fit.
 * - alignVert: boolean - Vertically align text.
 * - alignHoriz: boolean - Horizontally align text.
 * - minFontSize: number - Minimum font size in px.
 * - maxFontSize: number - Maximum font size in px.
 * - className: string - Custom class name.
 * - style: CSSProperties - Custom styles.
 */
import React, { useEffect, useRef, ReactNode, CSSProperties, Children, useMemo } from 'react';

export interface FitTextProps {
  children: ReactNode;
  alignVert?: boolean;
  alignHoriz?: boolean;
  minFontSize?: number;
  maxFontSize?: number;
  className?: string;
  lineClassName?: string;
  style?: CSSProperties;
}

const FitText: React.FC<FitTextProps> = ({
  children,
  alignVert = false,
  alignHoriz = false,
  minFontSize = 6,
  maxFontSize = 176,
  className = '',
  lineClassName = '',
  style = {},
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const linesRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const processTextFit = () => {
      const container = containerRef.current;
      const lineElements = linesRef.current;

      if (!container || lineElements.length === 0) return;

      const containerWidth = getInnerWidth(container);
      
      // Binary search for optimal font size
      let low = minFontSize;
      let high = maxFontSize;
      let optimalSize = low;

      while (low <= high) {
        const mid = Math.floor((high + low) / 2);
        
        // Apply font size to all line elements
        lineElements.forEach(lineEl => {
          if (lineEl) lineEl.style.fontSize = mid + 'px';
        });
        
        // Check if any line exceeds container width
        let anyLineOverflows = false;
        
        for (const lineEl of lineElements) {
          if (lineEl && lineEl.scrollWidth > containerWidth) {
            anyLineOverflows = true;
            break;
          }
        }

        if (!anyLineOverflows) {
          optimalSize = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }
      
      // Apply optimal font size to all lines
      lineElements.forEach(lineEl => {
        if (lineEl) lineEl.style.fontSize = optimalSize + 'px';
      });
    };

    // Calculate inner width without padding
    const getInnerWidth = (el: HTMLElement): number => {
      const style = window.getComputedStyle(el);
      return el.getBoundingClientRect().width -
        parseFloat(style.paddingLeft) -
        parseFloat(style.paddingRight);
    };

    // Initial processing
    const timer = setTimeout(processTextFit, 0);

    // Re-run on window resize
    const handleResize = () => {
      processTextFit();
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [children, minFontSize, maxFontSize]);

  // Convert children to array and create line elements
  const childArray = useMemo(() => Children.toArray(children), [children]);
  
  const containerStyle: CSSProperties = {
    ...style,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    textAlign: style.textAlign,
  };

  const lineStyle: CSSProperties = {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    textAlign: alignHoriz ? 'center' : undefined,
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={containerStyle}
    >
      {childArray.map((child, index) => (
        <div
          key={index}
          ref={el => {
            if (el) {
              linesRef.current[index] = el;
            }
          }}
          className={lineClassName}
          style={lineStyle}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

export default FitText;